import React, { useCallback, useContext, useState } from 'react';
import { Button, SuccessButton } from '../connectedForm/Button';
import DateField from '../connectedForm/DateInput/DateInput';
import Dropdown from '../connectedForm/Dropdown';
import * as FieldMetaData from '../../graphql/schema/fieldMetaData';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import getClientMutationId from '../../utils/getClientMutationId';
import ManualSalesOrderActivation from '../../graphql/mutations/sales/ManualSalesOrderActivation.graphql';
import {
  ISalesOrderManualActivationInput,
  ISalesOrderManualActivationMutationResult,
  ISalesOrderManualActivationMutationVariables,
  SalesOrderActivationType,
} from '../../graphql/schema/p4types';
import { useActionProps } from '../../utils/useActionProps';
import { ModalBottomButtonContainer } from './Styles';
import { BasicHeader } from '../../actions/BasicHeader';
import { IEnumMeta } from '../../graphql/schema/fieldMetaData';
import WorkspaceContext from '../Context/WorkspaceContext';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import FluentModal from './FluentModal';

const SalesOrderActivationModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { reloadData, selectionState } = useContext(WorkspaceContext);
  const [dateToActivateTo, setDateToActivateTo] = useState(null);
  const [salesOrderActivationTypes, setSalesOrderActivationTypes] = useState<
    Array<IEnumMeta<SalesOrderActivationType>>
  >([]);

  const [manualSalesOrderActivation] = useBackgroundMutation<
    ISalesOrderManualActivationMutationResult,
    ISalesOrderManualActivationMutationVariables
  >(ManualSalesOrderActivation, {
    jobTitle: 'Activating Orders',
    onStartJob: () => onClose(),
    onCompleteJob: () => reloadData?.(),
  });

  const actionProps = useActionProps('salesOrders');

  const activate = useCallback(() => {
    const input: ISalesOrderManualActivationInput = {
      clientMutationId: getClientMutationId(),
      ...normalizeVariables(actionProps),
      dateToActivateTo: dateToActivateTo,
      salesOrderActivationTypes: salesOrderActivationTypes.map((type) => type.value),
    };

    manualSalesOrderActivation({
      variables: {
        input: input,
      },
    });
  }, [actionProps, dateToActivateTo, manualSalesOrderActivation, salesOrderActivationTypes]);

  let title = 'Activate Orders';
  if (selectionState?.count) {
    title = `Activate Orders (${selectionState.count} selected)`;
  }

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { paddingBottom: '60px', minWidth: '600px' } }}>
      <BasicHeader>{title}</BasicHeader>
      <div style={{ padding: '1em' }}>
        <DateField
          data-testid={'date-to-activate-to'}
          label="Date to activate to"
          value={dateToActivateTo}
          onChange={setDateToActivateTo}
          portal={true}
        />
        <Dropdown
          dataid="outputType"
          options={FieldMetaData.default.SalesOrderActivationType.enumValues}
          label="Activation Type"
          onChange={setSalesOrderActivationTypes}
          value={salesOrderActivationTypes}
          valueKey="value"
          labelKey="label"
          multi={true}
        />
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="dispatchModal-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="dispatchModal-auto" onClick={activate}>
          Activate
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default SalesOrderActivationModal;
